import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, Search, X } from "lucide-react";
import api, { friendlyMessage } from "../lib/api";
import { createCustomerLookup, customerLabel, emptyResults, hasMore } from "../lib/customerPicker";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

/**
 * Searchable customer picker for admin forms.
 *
 * Collapsed it is one field showing the chosen customer (or a placeholder).
 * Tapping it opens a dialog with a search box and the store's customers, one
 * page at a time: typing searches by name or phone on the server, and
 * scrolling to the end (or "Load more") brings the next page. So any
 * customer of the store can be found, without ever loading them all.
 *
 * It replaced a plain <select> that could only offer the first 20 customers
 * — see lib/customerPicker.js.
 *
 * Laid out for a phone first: full-width dialog, 16px search text (no zoom
 * on focus in iOS), 48px rows, and a list that scrolls on its own so the
 * search box stays in view above the keyboard.
 *
 * Props:
 *   value       — the selected customer {id, name, phone}, or null
 *   onChange    — (customer) => void
 *   placeholder — collapsed-field text when nothing is selected
 *   testId      — data-testid of the collapsed field; parts are `${testId}-…`
 */
export default function CustomerPicker({ value = null, onChange, placeholder = "Pick customer…", testId = "customer-picker" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(emptyResults());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const lookup = useRef(null);
  if (!lookup.current) lookup.current = createCustomerLookup((url) => api.get(url));

  // `null` means a newer request superseded this one — keep what is shown.
  const run = (request) => {
    setBusy(true);
    setError(null);
    request()
      .then((next) => { if (next) { setResults(next); setBusy(false); } })
      .catch((e) => { setError(friendlyMessage(e)); setBusy(false); });
  };

  // Search on open and as the admin types (after a short pause).
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => run(() => lookup.current.search(query)), query ? 250 : 0);
    return () => clearTimeout(timer);
  }, [open, query]);

  const loadMore = () => { if (!busy && hasMore(results)) run(() => lookup.current.more()); };

  const onListScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 96) loadMore();
  };

  const choose = (customer) => {
    onChange?.(customer);
    setOpen(false);
  };

  // Closing forgets the search, so the next opening starts from the plain list.
  const onOpenChange = (next) => {
    setOpen(next);
    if (!next) { setQuery(""); setResults(emptyResults()); }
  };

  const typed = query.trim();
  // The list on screen answers `results.query`. Until the answer for what is
  // typed NOW arrives, say so — never label the old list with the new text.
  const pending = results.page === 0 || typed !== results.query;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
              className="h-11 px-4 rounded-2xl border border-neutral-200 bg-white w-full mt-1 flex items-center justify-between gap-2 text-left"
              data-testid={testId}>
        <span className={`truncate ${value ? "" : "text-neutral-500"}`}>{value ? customerLabel(value) : placeholder}</span>
        <ChevronDown className="w-4 h-4 shrink-0 text-neutral-500" />
      </button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-2xl p-4 sm:p-6 gap-3" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>Pick a customer</DialogTitle>
            <DialogDescription>Search by name or phone number.</DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} autoFocus
                   type="search" inputMode="search" enterKeyHint="search" autoComplete="off"
                   placeholder="Name or phone"
                   className="h-12 w-full rounded-2xl border border-neutral-200 bg-white pl-10 pr-10 text-base outline-none focus:border-brand-sky"
                   data-testid={`${testId}-search`} />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Clear search"
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-neutral-500"
                      data-testid={`${testId}-search-clear`}>
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="text-xs text-neutral-500 min-h-[1rem]" aria-live="polite" data-testid={`${testId}-count`}>
            {error ? "" : pending
              ? (typed ? "Searching…" : "Loading customers…")
              : results.total === 0
                ? (results.query ? `No customer matches “${results.query}”.` : "This store has no customers yet.")
                : `Showing ${results.items.length} of ${results.total}${results.query ? ` matching “${results.query}”` : " customers"}`}
          </div>

          <div onScroll={onListScroll}
               className={`max-h-[50vh] supports-[height:1dvh]:max-h-[50dvh] overflow-y-auto overscroll-contain -mx-1 px-1 ${pending ? "opacity-50" : ""}`}
               data-testid={`${testId}-list`}>
            {results.items.map((c) => {
              const selected = value?.id === c.id;
              return (
                <button key={c.id} type="button" onClick={() => choose(c)}
                        className={`w-full min-h-[48px] px-3 py-2 rounded-xl flex items-center gap-3 text-left ${selected ? "bg-brand-sky/10" : "hover:bg-neutral-50 active:bg-neutral-100"}`}
                        data-testid={`${testId}-option-${c.id}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold break-words">{c.name || "—"}</span>
                    <span className="block text-xs text-neutral-500">{c.phone}</span>
                  </span>
                  {selected && <Check className="w-4 h-4 shrink-0 text-brand-sky" />}
                </button>
              );
            })}

            {error && (
              <div className="py-4 text-center text-sm text-brand-clay" data-testid={`${testId}-error`}>
                {error}
                <button type="button" onClick={() => run(() => lookup.current.search(query))}
                        className="block mx-auto mt-2 h-11 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">
                  Retry
                </button>
              </div>
            )}

            {!error && hasMore(results) && (
              <button type="button" onClick={loadMore} disabled={busy}
                      className="w-full h-11 mt-1 rounded-2xl border border-neutral-200 text-sm font-bold text-brand-sky disabled:opacity-60"
                      data-testid={`${testId}-more`}>
                {busy ? "Loading…" : "Load more"}
              </button>
            )}

            {busy && !hasMore(results) && (
              <div className="py-3 flex justify-center" data-testid={`${testId}-loading`}>
                <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
